import type { NFT } from "@stepfinance/nft";
import type { Dispatch, SetStateAction } from "react";
import React, { useContext, useEffect, useState } from "react";

import { useCompressedNFTsQuery } from "../hooks/queries/nft/useCompressedNFTsQuery";
import { useNFTsWithTokensAndPricesByOwner } from "../hooks/queries/nft/useNFTsWithTokensAndPrices";
import { useAccountsContext } from "./accounts";
import { useAppConfig } from "./app/config";
import { useEnrichedWallet } from "./enrichedWallet";

interface NFTContextState {
  userNFTs: Map<string, NFT>;
  collectionList: string[];
  setUserNFTs: Dispatch<SetStateAction<Map<string, NFT>>>;
  setNFTBalance: (balance: number) => void;
  NFTBalance: number;
}

export type RefreshContextUpdaterProps = () => Promise<void>;

const NFTContext = React.createContext<null | NFTContextState>(null);

export function NFTContextProvider(props: any) {
  const { connected } = useEnrichedWallet();
  const { userTokenAccounts } = useAccountsContext();

  const [userNFTs, setUserNFTs] = useState<Map<string, NFT>>(new Map());
  const [collectionList, setCollectionList] = useState<string[]>([]);
  const [NFTBalance, setNFTBalance] = useState(0);

  const { hideCompressedNFTs } = useAppConfig();

  const { data: nftsResponse, status: nftsStatus, fetchStatus: nftsFetchStatus } = useNFTsWithTokensAndPricesByOwner();
  const { data: compressedNftsResponse, status: compressedNftsStatus } = useCompressedNFTsQuery();

  useEffect(() => {
    setUserNFTs(new Map());
    setCollectionList([]);
    setNFTBalance(0);
  }, [connected]);

  // Update userNFTs with data from NFTs query.
  useEffect(() => {
    const nftMap = new Map(userNFTs);
    const collectionList: string[] = [];
    if (nftsStatus === "success") {
      collectionList.push(...nftsResponse.collectionList);
      nftsResponse.nftMap.forEach((nft, mint) => {
        if (!nftMap.get(mint)) {
          nftMap.set(mint, nft);
        }
      });
      setNFTBalance(nftsResponse.totalValue);
    }
    // make sure cNFTs don't get pushed first so regular nft offchain metadata are fetched on mount.
    if (
      compressedNftsStatus === "success" &&
      userTokenAccounts.length > 0 &&
      (nftsStatus === "success" || nftsFetchStatus === "idle")
    ) {
      !hideCompressedNFTs && collectionList.push(...compressedNftsResponse.collectionList);
      compressedNftsResponse.compressedNftMap.forEach((nft, mint) => {
        const contextNft = nftMap.get(mint);
        if (!contextNft && !hideCompressedNFTs) {
          nftMap.set(mint, nft);
        }
        if (contextNft && hideCompressedNFTs) {
          nftMap.delete(mint);
        }
      });
    }
    setCollectionList(collectionList);
    setUserNFTs(nftMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nftsStatus, compressedNftsStatus, hideCompressedNFTs]);

  return (
    <NFTContext.Provider
      value={{
        userNFTs,
        collectionList,
        setUserNFTs,
        setNFTBalance,
        NFTBalance,
      }}
    >
      {props.children}
    </NFTContext.Provider>
  );
}

export function useNFTContext(): NFTContextState {
  const ctx = useContext(NFTContext);
  if (ctx === null) {
    throw new Error("Context not available");
  }
  return ctx;
}
